import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Ban, CheckCircle2, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const SUB_STATUSES = ["ACTIVE", "TRIALING", "PAST_DUE", "CANCELLED"];

export default function OrganizationDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const [busy, setBusy] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [sub, setSub] = useState({ plan_id: "", status: "ACTIVE", period_days: 30,
                                   notes: "", addons: [] });

  const load = useCallback(async () => {
    const { data } = await api.get(`/platform/organizations/${id}`);
    setData(data);
    setSub({
      plan_id: data.subscription?.plan_id || "",
      status: data.subscription?.status || "ACTIVE",
      period_days: 30,
      notes: data.subscription?.notes || "",
      addons: data.subscription?.addons || [],
    });
  }, [id]);

  useEffect(() => {
    load();
    api.get("/platform/plans").then(({ data }) => setPlans(data));
    api.get("/platform/modules").then(({ data }) => setModules(data));
  }, [load]);

  if (!data) return <div className="text-sm text-slate-500">Loading organisation…</div>;

  const org = data.organization;
  const account = data.account || { is_primary: true, companies: [] };
  const isPrimary = account.is_primary;
  const overrides = data.subscription?.module_overrides || {};
  const effective = new Set(data.effective_modules);

  const saveSubscription = async () => {
    if (!sub.plan_id) return toast.error("Pick a plan first");
    setBusy(true);
    try {
      const { data: fresh } = await api.put(`/platform/organizations/${id}/subscription`, {
        plan_id: sub.plan_id,
        status: sub.status,
        period_days: Number(sub.period_days) || 30,
        notes: sub.notes,
        addons: sub.addons,
      });
      setData(fresh);
      toast.success("Subscription updated");
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const toggleOverride = async (key, nextOn) => {
    // Absent key = follow the plan. Present = explicit force on/off for this org.
    const next = { ...overrides };
    const planAllows = plans.find((p) => p.id === data.subscription?.plan_id)?.module_keys?.includes(key);
    if (planAllows === nextOn) delete next[key];
    else next[key] = nextOn;
    try {
      const { data: fresh } = await api.put(`/platform/organizations/${id}/modules`, { overrides: next });
      setData(fresh);
      toast.success(`${key} ${nextOn ? "enabled" : "disabled"} for this organisation`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setStatus = async (status) => {
    try {
      await api.post(`/platform/organizations/${id}/status`, { status, reason });
      toast.success(status === "SUSPENDED" ? "Organisation suspended" : "Organisation reactivated");
      setSuspendOpen(false);
      setReason("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="org-detail-page">
      <div>
        <Link to="/platform/organizations" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
          <ArrowLeft size={13} /> All organizations
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">{org.name}</h1>
            <p className="mt-1 text-sm text-slate-500">
              <span className="font-mono">{org.gstin || "Unregistered"}</span>
              <span className="mx-2 text-slate-300">|</span>
              {org.city || "—"}, {org.state_name}
              <span className="mx-2 text-slate-300">|</span>
              Joined {fmtDate(org.created_at)}
            </p>
          </div>
          {org.status === "SUSPENDED" ? (
            <Button
              onClick={() => setStatus("ACTIVE")}
              data-testid="org-reactivate"
              className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckCircle2 size={15} /> Reactivate
            </Button>
          ) : (
            <Button
              onClick={() => setSuspendOpen(true)}
              data-testid="org-suspend"
              variant="outline"
              className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50"
            >
              <Ban size={15} /> Suspend
            </Button>
          )}
        </div>
      </div>

      {org.status === "SUSPENDED" && (
        <div
          className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
          data-testid="org-suspended-banner"
        >
          <strong>Suspended.</strong> Nobody in this organisation can sign in or use the API.
          {org.status_reason ? ` Reason: ${org.status_reason}` : ""}
        </div>
      )}

      {!isPrimary && (
        <div
          className="rounded-md border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900"
          data-testid="org-billed-through"
        >
          A Tally company under the{" "}
          <Link
            to={`/platform/organizations/${account.billing_organization_id}`}
            className="font-semibold underline underline-offset-2"
          >
            {account.billing_organization_name}
          </Link>{" "}
          account, and covered by its plan ({data.subscription_summary?.plan_name || "No plan"} ·{" "}
          {data.subscription_summary?.status || "NONE"}). Change the plan and modules there.
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-4">
        {[
          ["Tally companies", data.stats.companies],
          ["Vouchers", data.stats.vouchers],
          ["Ledgers", data.stats.ledgers],
          ["Active agents", data.stats.agents],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
            <div className="mt-1 font-heading text-2xl font-bold tabular-nums">{value}</div>
          </div>
        ))}
      </div>

      {account.companies.length > 1 && (
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold">
            Companies in this account ({account.companies.length})
          </div>
          <table className="w-full text-sm" data-testid="org-companies-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left font-semibold">Company</th>
                <th className="px-3 py-2 text-left font-semibold">Tally company</th>
                <th className="px-3 py-2 text-right font-semibold">Users</th>
                <th className="px-3 py-2 text-right font-semibold">Vouchers</th>
                <th className="px-4 py-2 text-left font-semibold">Added</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {account.companies.map((c) => (
                <tr key={c.id} className={c.id === org.id ? "bg-violet-50/40" : ""}>
                  <td className="px-4 py-2.5">
                    {c.id === org.id ? (
                      <span className="font-medium">{c.name}</span>
                    ) : (
                      <Link to={`/platform/organizations/${c.id}`} className="font-medium text-violet-700 hover:text-violet-900">
                        {c.name}
                      </Link>
                    )}
                    {c.is_primary && (
                      <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-600">
                        Pays
                      </span>
                    )}
                    {c.status === "SUSPENDED" && (
                      <span className="ml-2 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] uppercase text-rose-700">
                        Suspended
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{c.tally_company || "Not linked yet"}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{c.user_count}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{c.voucher_count}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{fmtDate(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isPrimary && (
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-3">
            <div className="text-sm font-semibold">Subscription</div>
            {account.companies.length > 1 && (
              <div className="mt-0.5 text-[11px] text-slate-500">
                Covers all {account.companies.length} companies in this account.
              </div>
            )}
          </div>
          <div className="space-y-3 p-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Plan</Label>
              <Select value={sub.plan_id} onValueChange={(v) => setSub((s) => ({ ...s, plan_id: v }))}>
                <SelectTrigger data-testid="org-plan-select">
                  <SelectValue placeholder="Choose a plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} · {inr(p.price_monthly)}/mo
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Status</Label>
                <Select value={sub.status} onValueChange={(v) => setSub((s) => ({ ...s, status: v }))}>
                  <SelectTrigger data-testid="org-sub-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUB_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Period (days)</Label>
                <Input
                  data-testid="org-period-days"
                  className="text-right font-mono"
                  value={sub.period_days}
                  onChange={(e) => setSub((s) => ({ ...s, period_days: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Internal note</Label>
              <Input
                data-testid="org-sub-notes"
                value={sub.notes}
                onChange={(e) => setSub((s) => ({ ...s, notes: e.target.value }))}
                placeholder="e.g. Annual deal, invoiced offline"
              />
            </div>
            {(() => {
              const plan = plans.find((p) => p.id === sub.plan_id);
              const offered = plan?.available_addons || [];
              if (!offered.length) return null;
              return (
                <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50/40 p-3">
                  <Label className="text-xs">Paid add-ons</Label>
                  {offered.map((key) => {
                    const m = modules.find((x) => x.key === key);
                    const on = sub.addons.includes(key);
                    return (
                      <label key={key} className="flex cursor-pointer items-center justify-between gap-3 text-sm"
                        data-testid={`org-addon-${key}`}>
                        <span className="min-w-0">
                          <span className="font-medium">{m?.name || key}</span>
                          <span className="ml-1.5 font-mono text-[11px] text-amber-700">
                            +{inr(m?.addon_price_monthly)}/mo
                          </span>
                        </span>
                        <Switch checked={on} onCheckedChange={(v) =>
                          setSub((sv) => ({
                            ...sv,
                            addons: v ? [...sv.addons, key] : sv.addons.filter((k) => k !== key),
                          }))} />
                      </label>
                    );
                  })}
                  <div className="border-t border-amber-200 pt-2 text-[11px] text-slate-600">
                    Plan {inr(plan?.price_monthly)}
                    {" + add-ons "}
                    {inr(sub.addons.reduce((t, k) =>
                      t + Number(modules.find((x) => x.key === k)?.addon_price_monthly || 0), 0))}
                    {" = "}
                    <strong className="font-mono">
                      {inr(Number(plan?.price_monthly || 0) + sub.addons.reduce((t, k) =>
                        t + Number(modules.find((x) => x.key === k)?.addon_price_monthly || 0), 0))}
                    </strong>
                    {" / month"}
                  </div>
                </div>
              );
            })()}

            <div className="flex items-center justify-between pt-1">
              <div className="text-[11px] text-slate-500">
                Renews {data.subscription?.current_period_end ? fmtDate(data.subscription.current_period_end) : "—"}
              </div>
              <Button
                onClick={saveSubscription}
                disabled={busy}
                data-testid="org-save-subscription"
                className="gap-1.5 bg-violet-600 hover:bg-violet-700"
              >
                {busy && <Loader2 size={14} className="animate-spin" />} Save subscription
              </Button>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-3">
            <div className="text-sm font-semibold">Modules</div>
            <div className="mt-0.5 text-[11px] text-slate-500">
              Overrides win over the plan. Core modules are always on.
            </div>
          </div>
          <div className="divide-y divide-slate-100" data-testid="org-modules-list">
            {modules.map((m) => {
              const on = effective.has(m.key);
              const overridden = Object.prototype.hasOwnProperty.call(overrides, m.key);
              return (
                <div key={m.key} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      {m.name}
                      {m.is_core && (
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-600">
                          Core
                        </span>
                      )}
                      {overridden && (
                        <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] uppercase text-violet-700">
                          Override
                        </span>
                      )}
                    </div>
                    <div className="truncate text-[11px] text-slate-500">{m.description}</div>
                  </div>
                  <Switch
                    checked={on}
                    disabled={m.is_core}
                    data-testid={`org-module-${m.key}`}
                    onCheckedChange={(v) => toggleOverride(m.key, v)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold">
          Members ({data.members.length})
        </div>
        <table className="w-full text-sm" data-testid="org-members-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left font-semibold">Name</th>
              <th className="px-3 py-2 text-left font-semibold">Email</th>
              <th className="px-3 py-2 text-left font-semibold">Role</th>
              <th className="px-4 py-2 text-left font-semibold">Joined</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.members.map((m) => (
              <tr key={m.id}>
                <td className="px-4 py-2.5 font-medium">
                  {m.name}
                  {m.is_owner && (
                    <span className="ml-2 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] uppercase text-blue-700">
                      Owner
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-slate-600">{m.email}</td>
                <td className="px-3 py-2.5 text-xs">{m.role}</td>
                <td className="px-4 py-2.5 text-xs text-slate-600">{fmtDate(m.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
        <DialogContent data-testid="suspend-dialog">
          <DialogHeader>
            <DialogTitle>Suspend {org.name}?</DialogTitle>
            <DialogDescription>
              Every user in this organisation is signed out and blocked from the API until you
              reactivate. Their data is untouched.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">Reason (shown to the tenant)</Label>
            <Input
              data-testid="suspend-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Payment overdue since 15 Aug"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendOpen(false)}>Cancel</Button>
            <Button
              onClick={() => setStatus("SUSPENDED")}
              data-testid="suspend-confirm"
              className="bg-rose-600 hover:bg-rose-700"
            >
              Suspend account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
